import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { LOGO_URL } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const genSessionId = () => {
  const existing = typeof window !== "undefined" && window.localStorage.getItem("clomi_sid");
  if (existing) return existing;
  const sid = "sid_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  if (typeof window !== "undefined") window.localStorage.setItem("clomi_sid", sid);
  return sid;
};

const GREETING = "Hi! I'm Clomi from ClomedixTech 👋 What can we help you build today? Whether it's a website, marketing campaign, video, or something custom — I'm here to help.";

const SUGGESTIONS = [
  "How much does a website cost?",
  "Do you run Google Ads?",
  "Can you make a brand logo?",
  "I want a mobile app",
];

const LEAD_MARKER = /\[LEAD_CAPTURED([^\]]*)\]/;

function parseLeadMarker(text) {
  const m = text.match(LEAD_MARKER);
  if (!m) return null;
  const attrs = m[1];
  const grab = (key) => {
    const r = new RegExp(`${key}\\s*=\\s*"([^"]*)"`, "i");
    const found = attrs.match(r);
    return found ? found[1].trim() : "";
  };
  return {
    name: grab("name"),
    phone: grab("phone"),
    service: grab("service"),
    notes: grab("notes"),
  };
}

function stripLeadMarker(text) { return text.replace(LEAD_MARKER, "").trim(); }

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sid] = useState(genSessionId);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const persistLead = async (lead) => {
    if (!lead || (!lead.name && !lead.phone)) return;
    try {
      await fetch(`${API}/leads/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sid,
          name: lead.name || "(not provided)",
          phone: lead.phone || "(not provided)",
          service: lead.service || "General enquiry",
          notes: lead.notes || "",
        }),
      });
    } catch { /* silent — lead already saved chat log side */ }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    const nextMessages = [...messages, { role: "user", content: text }, { role: "assistant", content: "" }];
    setMessages(nextMessages);
    setInput("");
    setSending(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: text, history }),
      });
      if (!res.ok || !res.body) throw new Error("Chat unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let leadCaptured = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        // Parse SSE lines
        for (const line of chunk.split(/\r?\n/)) {
          if (!line) continue;
          if (line.startsWith("event: done")) continue;
          if (line.startsWith("data: [DONE]")) continue;
          if (line.startsWith("event: error")) throw new Error(line.slice(13));
          if (line.startsWith("data: ")) {
            acc += line.slice(6);
            // detect and capture lead marker in-flight (only once)
            if (!leadCaptured) {
              const lead = parseLeadMarker(acc);
              if (lead) { leadCaptured = true; persistLead(lead); }
            }
            setMessages((prev) => {
              const clone = prev.slice(0, -1);
              return [...clone, { role: "assistant", content: stripLeadMarker(acc) }];
            });
          }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const clone = prev.slice(0, -1);
        return [...clone, { role: "assistant", content: "Sorry — I'm having trouble responding right now. You can WhatsApp us at +91 7010548247 or email info@clomedixtech.com." }];
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Open chat with Clomi"}
        data-testid="chatbot-toggle"
        className="fixed bottom-6 right-24 z-50 group"
      >
        <span className="absolute inset-0 rounded-full bg-purple-500/40 animate-pulse-ring" aria-hidden="true" />
        <span className="relative flex items-center justify-center w-14 h-14 rounded-full text-white shadow-2xl shadow-purple-500/40 hover:scale-110 transition-transform"
              style={{ background: "linear-gradient(135deg,#0F172A 0%,#7c3aed 50%,#ec4899 100%)" }}>
          {open ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
        </span>
        {!open && (
          <span className="absolute right-16 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-xs font-medium px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Chat with Clomi
          </span>
        )}
      </button>

      {/* Panel */}
      <div
        role="dialog"
        aria-hidden={!open}
        aria-label="ClomedixTech AI assistant Clomi"
        data-testid="chatbot-panel"
        className={`fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[380px] max-h-[70vh] bg-white rounded-3xl border border-slate-200 shadow-2xl overflow-hidden transition-all origin-bottom-right ${open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-90 pointer-events-none"}`}
        style={{ boxShadow: "0 30px 80px -20px rgba(147,51,234,0.35), 0 8px 24px rgba(15,23,42,0.12)" }}
      >
        {/* Header */}
        <div className="relative px-5 py-4 text-white overflow-hidden"
             style={{ background: "linear-gradient(135deg,#0F172A 0%,#7c3aed 55%,#ec4899 100%)" }}>
          <div className="absolute inset-0 opacity-30" aria-hidden="true"
               style={{ background: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35), transparent 40%), radial-gradient(circle at 90% 80%, rgba(255,255,255,0.15), transparent 40%)" }} />
          <div className="relative flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/95 p-1 shadow-lg">
              <img src={LOGO_URL} alt="ClomedixTech logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading font-bold text-base leading-tight">Clomi · AI Assistant</div>
              <div className="text-[11px] opacity-80 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.9)]" />
                Online · Replies instantly
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="p-4 overflow-y-auto space-y-3 no-scrollbar" style={{ maxHeight: "calc(70vh - 180px)" }} data-testid="chatbot-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-slate-900 text-white rounded-br-sm"
                    : "bg-slate-50 text-slate-800 rounded-bl-sm border border-slate-100"
                }`}
              >
                {m.content || (sending && i === messages.length - 1 ? (
                  <span className="inline-flex items-center gap-1 text-slate-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "240ms" }} />
                  </span>
                ) : "")}
              </div>
            </div>
          ))}
        </div>

        {/* Suggestions */}
        {messages.length <= 1 && !sending && (
          <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="chatbot-suggestions">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-[11px] px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 hover:bg-brand-gradient hover:text-white hover:border-transparent transition-all"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-slate-100 p-3 flex items-center gap-2 bg-white"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message…"
            disabled={sending}
            data-testid="chatbot-input"
            className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-200 transition-all"
            aria-label="Message"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            aria-label="Send message"
            data-testid="chatbot-send"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed transition-transform hover:scale-105"
            style={{ background: "linear-gradient(135deg,#0F172A 0%,#7c3aed 55%,#ec4899 100%)" }}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </>
  );
};
